import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { validarServicosOS } from '@/lib/os-servicos';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Package } from 'lucide-react';
import { usePrestadores } from '@/hooks/usePrestadores';
import { useProfilesComRoles } from '@/hooks/useProfiles';
import { filtrarPrestadoresAtribuiveis } from '@/lib/prestadores-atribuiveis';
import { useAtualizarRecorrencia } from '@/hooks/useRecorrencias';
import { useServicosAtivos } from '@/hooks/useServicos';
import { useAuthContext } from '@/contexts/AuthContext';
import { SeletorServicos } from '@/components/ordens/SeletorServicos';
import type { FrequenciaRecorrencia, RecorrenciaOS } from '@/types/recorrencia';
import type { EnderecoOS } from '@/types/ordemServico';
import type { ItemSelecionado } from '@/types/servico';
import { calcularTotalItens, formatarValor } from '@/types/servico';
import { construirItensOS, construirServicosLegacy } from '@/lib/os-itens-builder';

interface RecorrenciaEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recorrencia: RecorrenciaOS;
}

export function RecorrenciaEditDialog({
  open,
  onOpenChange,
  recorrencia,
}: RecorrenciaEditDialogProps) {
  const { t } = useTranslation();
  const { organization } = useAuthContext();
  const atualizar = useAtualizarRecorrencia();
  // Atribuir um prestador é planeamento do admin/atendente — lista prestadores
  // de membros ativos (`organization_members.ativo = true`).
  const { data: prestadoresRaw, isLoading: loadingPrestadores } = usePrestadores();
  const { data: prestadoresAtivos } = useProfilesComRoles('prestador');
  const prestadores = useMemo(
    () =>
      filtrarPrestadoresAtribuiveis(
        prestadoresRaw ?? [],
        new Set((prestadoresAtivos ?? []).map((p) => p.id)),
      ),
    [prestadoresRaw, prestadoresAtivos],
  );
  const { data: servicosAtivos } = useServicosAtivos();

  const [frequencia, setFrequencia] = useState<FrequenciaRecorrencia>(recorrencia.frequencia);
  const [proximaData, setProximaData] = useState<string>('');
  const [prestadorId, setPrestadorId] = useState<string>('');
  const [observacoes, setObservacoes] = useState<string>('');

  const [itensSelecionados, setItensSelecionados] = useState<ItemSelecionado[]>([]);

  const [rua, setRua] = useState<string>('');
  const [numero, setNumero] = useState<string>('');
  const [complemento, setComplemento] = useState<string>('');
  const [codigoPostal, setCodigoPostal] = useState<string>('');
  const [cidade, setCidade] = useState<string>('');
  const [regiao, setRegiao] = useState<string>('');
  const [pais, setPais] = useState<string>('');

  const valorTotal = calcularTotalItens(itensSelecionados);

  useEffect(() => {
    if (!open) return;
    setFrequencia(recorrencia.frequencia);
    // datetime-local expects "YYYY-MM-DDTHH:mm"
    setProximaData(recorrencia.proxima_data.slice(0, 16));
    setPrestadorId(recorrencia.prestador_id || '');
    setObservacoes(recorrencia.observacoes || '');

    setRua(recorrencia.endereco?.rua || '');
    setNumero(recorrencia.endereco?.numero || '');
    setComplemento(recorrencia.endereco?.complemento || '');
    setCodigoPostal(recorrencia.endereco?.codigo_postal || '');
    setCidade(recorrencia.endereco?.cidade || '');
    setRegiao(recorrencia.endereco?.regiao || '');
    setPais(recorrencia.endereco?.pais || '');
  }, [open, recorrencia]);

  // Convert stored itens → ItemSelecionado[] once the catalog is available.
  // Mirrors the legacy-snapshot loader in OrdemServicoFormDialog: if a service
  // no longer exists in the catalog we still render it from the snapshot.
  useEffect(() => {
    if (!open) return;
    if (!recorrencia.itens || recorrencia.itens.length === 0) {
      setItensSelecionados([]);
      return;
    }
    if (!servicosAtivos) return;

    const convertidos: ItemSelecionado[] = recorrencia.itens.map((item) => {
      const catalogo = servicosAtivos.find((s) => s.id === item.servico_id);
      return {
        servico: {
          id: item.servico_id,
          nome: item.servico_nome,
          descricao: catalogo?.descricao || '',
          tipo_preco: catalogo?.tipo_preco || 'fixo',
          valor_base: item.valor_unitario,
          unidade: catalogo?.unidade || 'serviço',
          duracao_padrao_minutos: catalogo?.duracao_padrao_minutos ?? null,
          ativo: true,
          criado_em: '',
          atualizado_em: '',
        },
        quantidade: item.quantidade,
      };
    });
    setItensSelecionados(convertidos);
  }, [open, recorrencia, servicosAtivos]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validacaoServicos = validarServicosOS(itensSelecionados);
    if (!validacaoServicos.ok) {
      toast.error(t('orders.form.singleHourlyService'));
      return;
    }

    const endereco: EnderecoOS = {
      rua: rua || undefined,
      numero: numero || undefined,
      complemento: complemento || undefined,
      codigo_postal: codigoPostal || undefined,
      cidade: cidade || undefined,
      regiao: regiao || undefined,
      pais: pais || undefined,
    };

    const servicosLegacy = construirServicosLegacy(itensSelecionados);
    const itens = construirItensOS(itensSelecionados);

    await atualizar.mutateAsync({
      id: recorrencia.id,
      dados: {
        frequencia,
        proxima_data: proximaData ? new Date(proximaData).toISOString() : undefined,
        prestador_id: prestadorId || null,
        observacoes,
        servicos: servicosLegacy,
        itens,
        endereco,
        valor_total: valorTotal,
      },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] !overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('recurrences.edit.title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label>{t('recurrences.edit.frequencyLabel')}</Label>
            <Select
              value={frequencia}
              onValueChange={(v) => setFrequencia(v as FrequenciaRecorrencia)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="semanal">{t('recurrences.frequency.weekly')}</SelectItem>
                <SelectItem value="quinzenal">{t('recurrences.frequency.biweekly')}</SelectItem>
                <SelectItem value="mensal">{t('recurrences.frequency.monthly')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('recurrences.edit.nextDateLabel')}</Label>
            <Input
              type="datetime-local"
              value={proximaData}
              onChange={(e) => setProximaData(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('recurrences.edit.providerLabel')}</Label>
            <Select
              value={prestadorId || '_none_'}
              onValueChange={(v) => setPrestadorId(v === '_none_' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">{t('recurrences.edit.providerNone')}</SelectItem>
                {loadingPrestadores ? (
                  <div className="p-2 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </div>
                ) : (
                  prestadores?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.profile?.nome || t('common.noName')}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              {t('orders.form.services')}
            </Label>
            <SeletorServicos value={itensSelecionados} onChange={setItensSelecionados} />
          </div>

          <div className="space-y-2">
            <Label>{t('orders.form.totalValue')}</Label>
            <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg text-center">
              <span className="text-xl font-bold text-primary">
                {formatarValor(valorTotal)}
              </span>
              <p className="text-xs text-muted-foreground mt-1">
                {t('orders.calculatedAutomatically')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Label className="text-base font-semibold">
              {t('orders.form.serviceAddress')}
            </Label>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>{t('orders.form.street')}</Label>
                <Input
                  value={rua}
                  onChange={(e) => setRua(e.target.value)}
                  placeholder={t('orders.form.streetPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('orders.form.number')}</Label>
                <Input
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                  placeholder={t('orders.form.numberPlaceholder')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('orders.form.complement')}</Label>
              <Input
                value={complemento}
                onChange={(e) => setComplemento(e.target.value)}
                placeholder={t('orders.form.complementPlaceholder')}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('orders.form.postalCode')}</Label>
                <Input
                  value={codigoPostal}
                  onChange={(e) => setCodigoPostal(e.target.value)}
                  placeholder={t('orders.form.postalCodePlaceholder')}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>{t('orders.form.city')}</Label>
                <Input
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value)}
                  placeholder={t('orders.form.cityPlaceholder')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('orders.form.region')}</Label>
                <Input
                  value={regiao}
                  onChange={(e) => setRegiao(e.target.value)}
                  placeholder={t('orders.form.regionPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('orders.form.country')}</Label>
                <Input
                  value={pais}
                  onChange={(e) => setPais(e.target.value)}
                  placeholder={organization?.pais ?? t('orders.form.countryPlaceholder')}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('recurrences.edit.notesLabel')}</Label>
            <Textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
            />
          </div>

          <p className="text-xs text-muted-foreground">{t('recurrences.edit.hint')}</p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={atualizar.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={atualizar.isPending}>
              {atualizar.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('recurrences.edit.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
